import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, CaretDown } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function ImportQuestionsButton({ onImported }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/questions/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${res.data.created} questions`);
      onImported && onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const downloadTemplate = async (fmt) => {
    try {
      const res = await api.get(`/questions/template?format=${fmt}`, { responseType: "blob" });
      const ext = fmt === "excel" ? "xlsx" : fmt;
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `questions_template.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Template downloaded (${ext.toUpperCase()})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Template download failed");
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".json,.csv,.xlsx,.xls"
        onChange={handleFile}
        className="hidden"
        data-testid="import-file-input"
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            disabled={busy}
            className="border border-zinc-300 px-4 py-2.5 text-sm font-medium hover:bg-zinc-50 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="import-questions-btn"
          >
            <UploadSimple size={14} />
            {busy ? "Importing…" : "Import"}
            <CaretDown size={12} weight="bold" className="ml-1" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Upload questions</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid="import-open-file-picker"
          >
            <UploadSimple size={14} /> Upload file (.csv/.xlsx/.json)
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Download template</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => downloadTemplate("xlsx")}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid="template-download-xlsx"
          >
            <DownloadSimple size={14} className="text-emerald-600" /> Excel (.xlsx)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => downloadTemplate("csv")}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid="template-download-csv"
          >
            <DownloadSimple size={14} className="text-blue-600" /> CSV (.csv)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => downloadTemplate("json")}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid="template-download-json"
          >
            <DownloadSimple size={14} className="text-amber-600" /> JSON (.json)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
